import { CALENDAR_CONFIG, CONTACT_CONFIG } from '../../config/constants.js'
import { decideCompanyBinding, emailDomain, isGenericOrFreeDomain } from '../../lib/company-binding.js'
import { BOOKING_CARD_MARKER } from '../../lib/booking-markdown.js'

/**
 * Workshop intake. One short question at a time.
 * The prompt text in this file must not name a customer.
 * Live on Fly does not run this. The browser does, on the same session.
 */
export const QUESTION_WORD_CAP = 16
/**
 * Spoken cap: the consent line plus six topics.
 * Security and GDPR are one topic. Audience is the sixth.
 */
export const MAX_INTAKE_QUESTIONS = 7

export type IntakeLanguage = 'nb' | 'nn' | 'en'
export type IntakeSource = 'chat' | 'admin' | 'research'
export type IntakeFieldKey =
  | 'company'
  | 'stack'
  | 'models'
  | 'security'
  | 'gdpr'
  | 'use'
  | 'goal'
  | 'level'
  | 'problem'
  | 'audience'

export interface IntakeField {
  value: string
  source: IntakeSource
  at: string
  gap: boolean
}

export type WorkshopAskNoun = 'workshop' | 'kurs' | 'opplæring'

export interface WorkshopIntake {
  language: IntakeLanguage
  consent: boolean
  stopped: boolean
  done: boolean
  /** Questions already spoken, including the opener. */
  questionsAsked: number
  pending: IntakeFieldKey | 'consent' | 'implementation' | null
  fields: Partial<Record<IntakeFieldKey, IntakeField>>
  /** The customer's own word for the ask. Repeated questions keep it. */
  askedAs?: WorkshopAskNoun
  /** The same line asked for implementation and a course. Both stay in the summary. */
  combinedWithImplementation?: boolean
}

export interface IntakeAdvanceResult {
  handled: boolean
  reply: string | null
  intake: WorkshopIntake
  done: boolean
}

/** Mirror the word the customer used. «kurs» is not rewritten as «workshop». */
export function workshopAskNoun(text: string): WorkshopAskNoun {
  if (/\b(opplæring|opplaering|opplaring)\b/i.test(text)) return 'opplæring'
  if (/\bkurs\b/i.test(text)) return 'kurs'
  return 'workshop'
}

export function workshopOpener(language: IntakeLanguage, noun: WorkshopAskNoun = 'workshop'): string {
  if (language === 'en') {
    const word = noun === 'kurs' ? 'a course' : noun === 'opplæring' ? 'training' : 'a workshop'
    return `You asked for ${word}. May I save your answers and ask a few short questions?`
  }
  if (language === 'nn') {
    const phrase = noun === 'kurs' ? 'eit kurs' : noun === 'opplæring' ? 'opplæring' : 'ein workshop'
    return `Sidan de ønskjer ${phrase}, kan eg lagre svara og stille nokre korte spørsmål?`
  }
  const phrase = noun === 'kurs' ? 'et kurs' : noun === 'opplæring' ? 'opplæring' : 'en workshop'
  return `Siden dere ønsker ${phrase}, kan jeg lagre svarene og stille noen korte spørsmål?`
}

const QUESTIONS: Record<IntakeFieldKey, Record<IntakeLanguage, string>> = {
  company: {
    nb: 'Hva heter bedriften?',
    nn: 'Kva heiter bedrifta?',
    en: 'What is the company called?',
  },
  stack: {
    nb: 'Bruker dere ChatGPT, Copilot eller noe annet i dag?',
    nn: 'Bruker de ChatGPT, Copilot eller noko anna i dag?',
    en: 'Do you use ChatGPT, Copilot, or something else today?',
  },
  models: {
    nb: 'Bruker dere ChatGPT, Copilot eller noe annet i dag?',
    nn: 'Bruker de ChatGPT, Copilot eller noko anna i dag?',
    en: 'Do you use ChatGPT, Copilot, or something else today?',
  },
  use: {
    nb: 'Er resultatet til dere selv, eller også til noen utenfor?',
    nn: 'Er resultatet til dykk sjølve, eller også til nokon utanfor?',
    en: 'Is the result for your own team, or for people outside?',
  },
  goal: {
    nb: 'Hva skal bli bedre etter {format}?',
    nn: 'Kva skal bli betre etter {format}?',
    en: 'What should be better after {format}?',
  },
  problem: {
    nb: 'Hvilken oppgave skal dere ta i {place}?',
    nn: 'Kva oppgåve skal de ta i {place}?',
    en: 'Which task should you work on in {place}?',
  },
  security: {
    nb: 'Kan teksten inneholde kundedata eller personopplysninger?',
    nn: 'Kan teksten innehalde kundedata eller personopplysningar?',
    en: 'Can the text include customer data or personal data?',
  },
  gdpr: {
    nb: 'Kan teksten inneholde kundedata eller personopplysninger?',
    nn: 'Kan teksten innehalde kundedata eller personopplysningar?',
    en: 'Can the text include customer data or personal data?',
  },
  level: {
    nb: 'Skal dere bare bruke verktøyet, eller også bygge en flyt?',
    nn: 'Skal de berre bruke verktøyet, eller også byggje ein flyt?',
    en: 'Will you only use the tool, or also build a flow?',
  },
  audience: {
    nb: 'Hvem skal være med, og er de vant med slike verktøy?',
    nn: 'Kven skal vere med, og er dei vane med slike verktøy?',
    en: 'Who will attend, and are they used to these tools?',
  },
}

/**
 * Topics after consent. Stack also fills models. Security also fills GDPR.
 * Level comes from the audience answer and is not its own question.
 * Company is never asked. The plate already has it.
 */
const ASK_ORDER: IntakeFieldKey[] = ['stack', 'use', 'goal', 'problem', 'security', 'audience']

const CLOSE: Record<IntakeLanguage, string> = {
  nb: 'Takk. Jeg har det jeg trenger. Neste steg er en gratis samtale på 30 minutter med Farzad.',
  nn: 'Takk. Eg har det eg treng. Neste steg er ein gratis samtale på 30 minutt med Farzad.',
  en: 'Thank you. I have what I need. The next step is a free 30-minute call with Farzad.',
}

/** Successful close only. No price, no package, no pitch. The call is the close. */
export function workshopCloseReply(language: IntakeLanguage, bookingUrl?: string): string {
  const url = bookingUrl?.trim() || CALENDAR_CONFIG.getLink('workshop')
  return `${CLOSE[language]}\n\n[Book a time with Farzad](${url})\n\n${BOOKING_CARD_MARKER}`
}

const STOPPED: Record<IntakeLanguage, string> = {
  nb: 'Den er grei. Vi stopper der.',
  nn: 'Den er grei. Vi stoppar der.',
  en: 'All right. We stop there.',
}

const DECLINED: Record<IntakeLanguage, string> = {
  nb: 'Greit. Jeg lagrer ingen svar.',
  nn: 'Greitt. Eg lagrar ingen svar.',
  en: 'All right. I will not save any answers.',
}

const INTERNAL_ONLY: Record<IntakeLanguage, string> = {
  nb: 'Bare internt.',
  nn: 'Berre internt.',
  en: 'Internal only.',
}

const NO_PERSONAL_DATA: Record<IntakeLanguage, string> = {
  nb: 'Ingen personopplysninger.',
  nn: 'Ingen personopplysningar.',
  en: 'No personal data.',
}

export function questionWordCount(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length
}

export function isSingleQuestion(text: string): boolean {
  return (text.match(/\?/g) ?? []).length === 1 && questionWordCount(text) <= QUESTION_WORD_CAP
}

/** The noun in a later question. One word, definite, matching askedAs. */
export function workshopFormatPhrase(language: IntakeLanguage, noun: WorkshopAskNoun = 'workshop'): string {
  if (language === 'en') {
    if (noun === 'kurs') return 'the course'
    if (noun === 'opplæring') return 'the training'
    return 'the workshop'
  }
  if (language === 'nn') {
    if (noun === 'kurs') return 'kurset'
    if (noun === 'opplæring') return 'opplæringa'
    return 'workshopen'
  }
  if (noun === 'kurs') return 'kurset'
  if (noun === 'opplæring') return 'opplæringen'
  return 'workshopen'
}

/** Where the task happens. Workshop keeps the room. Kurs and opplæring use the asked noun. */
export function workshopPlacePhrase(language: IntakeLanguage, noun: WorkshopAskNoun = 'workshop'): string {
  if (language === 'en') {
    if (noun === 'kurs') return 'the course'
    if (noun === 'opplæring') return 'the training'
    return 'the room'
  }
  if (language === 'nn') {
    if (noun === 'kurs') return 'kurset'
    if (noun === 'opplæring') return 'opplæringa'
    return 'rommet'
  }
  if (noun === 'kurs') return 'kurset'
  if (noun === 'opplæring') return 'opplæringen'
  return 'rommet'
}

export function intakeQuestion(key: IntakeFieldKey, language: IntakeLanguage, noun: WorkshopAskNoun = 'workshop'): string {
  const template = QUESTIONS[key][language]
  return template
    .split('{format}')
    .join(workshopFormatPhrase(language, noun))
    .split('{place}')
    .join(workshopPlacePhrase(language, noun))
}

export function intakeQuestionScript(language: IntakeLanguage, noun: WorkshopAskNoun = 'workshop'): string[] {
  return [workshopOpener(language, noun), ...ASK_ORDER.map(key => intakeQuestion(key, language, noun))]
}

export function emptyWorkshopIntake(language: IntakeLanguage): WorkshopIntake {
  return {
    language,
    consent: false,
    stopped: false,
    done: false,
    questionsAsked: 0,
    pending: null,
    fields: {},
  }
}

function field(value: string, at: string, gap: boolean, source: IntakeSource = 'chat'): IntakeField {
  return { value, source, at, gap }
}

export interface ResearchIntakeInput {
  company?: {
    name?: string | null
    summary?: string | null
    description?: string | null
    industry?: string | null
  } | null
  strategic?: {
    pain_points?: string[] | null
    internal_gap?: string | null
    digital_maturity?: string | null
  } | null
  groundedPack?: {
    company?: { summary?: string | null } | null
    pressurePoints?: Array<{ claim?: string | null }> | null
    aiAutomationAngle?: { issue?: string | null; example?: string | null } | null
  } | null
}

function researchText(research: ResearchIntakeInput): string {
  const points = [
    ...(research.strategic?.pain_points ?? []),
    ...(research.groundedPack?.pressurePoints ?? []).map(row => row.claim ?? ''),
  ]
  return [
    research.company?.summary,
    research.company?.description,
    research.company?.industry,
    research.groundedPack?.company?.summary,
    research.groundedPack?.aiAutomationAngle?.issue,
    research.groundedPack?.aiAutomationAngle?.example,
    research.strategic?.internal_gap,
    research.strategic?.digital_maturity,
    ...points,
  ]
    .filter((line): line is string => Boolean(line && line.trim()))
    .join('\n')
}

function researchProblem(research: ResearchIntakeInput): string | null {
  const pain = research.strategic?.pain_points?.find(line => line.trim())
  if (pain) return pain.trim()
  const pressure = research.groundedPack?.pressurePoints?.find(row => row.claim?.trim())?.claim
  if (pressure?.trim()) return pressure.trim()
  const issue = research.groundedPack?.aiAutomationAngle?.issue?.trim()
  return issue || null
}

/** Fields a research turn already answered. Intake must not ask these again. */
export function intakeFieldsFromResearch(
  research: ResearchIntakeInput | null | undefined,
  language: IntakeLanguage
): Partial<Record<IntakeFieldKey, string>> {
  if (!research) return {}
  const text = researchText(research)
  const known: Partial<Record<IntakeFieldKey, string>> = {}
  const companyName = research.company?.name?.trim()
  if (companyName && companyName.length >= 2) known.company = companyName.slice(0, 120)
  const toolLine = [
    research.company?.summary,
    research.company?.description,
    research.groundedPack?.company?.summary,
    research.groundedPack?.aiAutomationAngle?.example,
    research.strategic?.digital_maturity,
  ].find(line => line && mentionedTool(line))
  if (toolLine?.trim()) {
    known.stack = toolLine.trim().slice(0, 400)
    known.models = known.stack
  }
  const problem = researchProblem(research)
  if (problem) known.problem = problem.slice(0, 400)
  if (/\b(internt|intern bruk|bare internt|berre internt|internal only|inside the (firm|company|team)|for internal use)\b/i.test(text)) {
    known.use = INTERNAL_ONLY[language]
  }
  if (/\b(ingen personopplysninger|ingen personopplysningar|no personal data|without personal data)\b/i.test(text)) {
    const value = NO_PERSONAL_DATA[language]
    known.security = value
    known.gdpr = value
  }
  const level = inferLevel(text)
  if (level) known.level = String(level)
  return known
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  return value as Record<string, unknown>
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

function asStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
}

/** Read the session research object. Company name and industry are known, and are not questions. */
export function intakeFieldsFromResearchContext(
  researchContext: unknown,
  company: { name?: string | null; summary?: string | null; industry?: string | null } | null,
  language: IntakeLanguage
): Partial<Record<IntakeFieldKey, string>> {
  const context = asRecord(researchContext)
  const researchCompany = asRecord(context?.company)
  const strategic = asRecord(context?.strategic)
  const pack = asRecord(context?.groundedPack)
  const packCompany = asRecord(pack?.company)
  const angle = asRecord(pack?.aiAutomationAngle)
  const pressure = Array.isArray(pack?.pressurePoints) ? pack.pressurePoints : []
  return intakeFieldsFromResearch(
    {
      company: {
        name: asString(researchCompany?.name) ?? company?.name ?? null,
        summary: asString(researchCompany?.summary) ?? asString(packCompany?.summary) ?? company?.summary ?? null,
        description: asString(researchCompany?.description),
        industry: asString(researchCompany?.industry) ?? company?.industry ?? null,
      },
      strategic: strategic
        ? {
            pain_points: asStringList(strategic.pain_points),
            internal_gap: asString(strategic.internal_gap),
            digital_maturity: asString(strategic.digital_maturity),
          }
        : null,
      groundedPack: pack
        ? {
            company: { summary: asString(packCompany?.summary) },
            pressurePoints: pressure.map(row => ({ claim: asString(asRecord(row)?.claim) })),
            aiAutomationAngle: angle
              ? { issue: asString(angle.issue), example: asString(angle.example) }
              : null,
          }
        : null,
    },
    language
  )
}

/**
 * Discovery brief fields the visitor already stated. Reuse them.
 * Do not ask the stack, the task, or the goal again.
 * Company is not copied from here. The plate already has it.
 */
export function intakeFieldsFromSessionFacts(
  sessionFacts: unknown,
  language: IntakeLanguage
): Partial<Record<IntakeFieldKey, string>> {
  void language
  const facts = asRecord(sessionFacts)
  if (!facts) return {}
  const read = (key: string): string => {
    const row = asRecord(facts[key])
    return asString(row?.value) ?? ''
  }
  const known: Partial<Record<IntakeFieldKey, string>> = {}
  // The stored fact is an internal line («Current tool context: gemini»). Keep only the tools.
  const tech = read('techContext')
    .replace(/^Current tool context:\s*/i, '')
    .split(/\.\s/)[0]!
    .split(/,\s*/)
    .map(tool => tool.trim())
    .filter(Boolean)
    .map(tool => (/[A-Z]/.test(tool) ? tool : tool.replace(/\b\p{L}/gu, ch => ch.toUpperCase())))
    .join(', ')
  if (tech.length >= 2) {
    known.stack = tech.slice(0, 400)
    known.models = known.stack
  }
  const problem = read('problem')
  if (problem.length >= 2) known.problem = problem.slice(0, 400)
  const goal = read('desiredResult')
  if (goal.length >= 2) known.goal = goal.slice(0, 400)
  return known
}

function seedResearchFields(
  intake: WorkshopIntake,
  known: Partial<Record<IntakeFieldKey, string>> | undefined,
  at: string
): void {
  if (!known) return
  for (const key of Object.keys(known) as IntakeFieldKey[]) {
    const value = known[key]?.trim()
    if (!value || intake.fields[key]) continue
    intake.fields[key] = field(value.slice(0, 400), at, false, 'research')
  }
  if (intake.fields.stack && !intake.fields.models) {
    intake.fields.models = { ...intake.fields.stack }
  }
  if (intake.fields.security && !intake.fields.gdpr) {
    intake.fields.gdpr = { ...intake.fields.security }
  }
}

function seedBriefFields(
  intake: WorkshopIntake,
  brief: Partial<Record<IntakeFieldKey, string>> | undefined,
  at: string
): void {
  if (!brief) return
  for (const key of ['stack', 'models', 'problem', 'goal'] as const) {
    const value = brief[key]?.trim()
    if (!value) continue
    const existing = intake.fields[key]
    if (existing && existing.source !== 'research') continue
    intake.fields[key] = field(value.slice(0, 400), at, false, 'chat')
  }
}

function isUnknown(text: string): boolean {
  return /\b(vet ikke|veit ikke|veit ikkje|usikker|aner ikke|don't know|do not know|not sure|unsure|no idea)\b/i.test(
    text
  )
}

/**
 * Workshop answers stay on the workshop intake. They are not the Strategic Brief extractor.
 * That extractor keeps customer lines only and drops a fragment under 20 characters.
 * «Bare internt» is kept. «vet ikke» is a gap with an empty value, not a finding.
 */
export function keepWorkshopAnswer(text: string): 'empty' | 'gap' | 'keep' {
  const value = text.trim()
  if (!value) return 'empty'
  if (isUnknown(value)) return 'gap'
  return 'keep'
}

/**
 * A stop is a request to end the questions, not a word inside an answer.
 * N21 (17.10): «…og vi vil ha det ferdig før jul» matched «ferdig» and ended the intake.
 * «ferdig» stops only as the whole line («Ferdig», «Jeg er ferdig»), and «ikke flere» only
 * with «spørsmål» or alone. Boundaries are Unicode-aware, so «nok nå» matches too.
 */
const STOP_ANYWHERE =
  /(?<![\p{L}\p{N}])(stopp|stop|det holder|nok nå|nok no|that's enough|that is enough|no more|ikke flere spørsmål|ikkje fleire spørsmål)(?![\p{L}\p{N}])/iu
const STOP_WHOLE_LINE =
  /^(?:(?:ok|okei|greit|takk)[\s,.!]+)?(?:(?:jeg|eg|vi)\s+er\s+)?(?:ferdig|ikke flere|ikkje fleire)(?:\s+(?:nå|no|for nå|for no))?(?:[\s,.!]+takk)?[\s.!]*$/iu

function wantsStop(text: string): boolean {
  const value = text.trim()
  return STOP_ANYWHERE.test(value) || STOP_WHOLE_LINE.test(value)
}

function isYes(text: string): boolean {
  return /^(ja|jepp|yes|yeah|yep|ok|okay|gjerne|greit|sure|absolutt)\b/i.test(text.trim())
}

function isNo(text: string): boolean {
  return /^(nei|no|nope)\b/i.test(text.trim())
}

/** A training word that may accompany "adoption". Bare "team" is not one. */
const TRAINING_PHRASE =
  /\b(?:workshop|bootcamp|opplæring|opplaering|opplaring|training|train|learn|lære|laere|kurs|teach|course|upskill(?:ing)?)\b/i

/**
 * Explicit or implicit workshop ask. Ordinary discovery lines do not match.
 * "Upskill" is itself a training ask. "Adoption" counts only on the same line as a training word.
 */
const IMPLEMENTATION_ASK =
  /\b(implementering|implementation|innføring|innforing|konsulent|consulting)\b/i

/** A course and an implementation on the same line. Intake alone would drop the implementation. */
export function isCombinedConsultingAndTraining(text: string): boolean {
  return isWorkshopAsk(text) && IMPLEMENTATION_ASK.test(text)
}

export function combinedNeedLabel(language: IntakeLanguage): string {
  if (language === 'en') return 'AI implementation + course'
  return 'KI-implementering + kurs'
}

function implementationAsk(language: IntakeLanguage): string {
  if (language === 'en') {
    return 'You asked for both an AI implementation and a course. What should the implementation solve?'
  }
  if (language === 'nn') {
    return 'De vil ha både KI-implementering og eit kurs. Kva skal implementeringa løyse?'
  }
  return 'Dere vil ha både KI-implementering og et kurs. Hva skal implementeringen løse?'
}

export function isWorkshopAsk(text: string): boolean {
  return (
    /\b(workshop|bootcamp|opplæring|opplaering|opplaring)\b/i.test(text) ||
    /\bkurs\b/i.test(text) ||
    /\b(lære|laere)\s+(ansatte|teamet|dei\s+tilsette|de\s+ansatte)\b/i.test(text) ||
    /\b(train|teach)\s+the\s+(staff|team)\b/i.test(text) ||
    /\b(staff|employee)\s+training\b/i.test(text) ||
    /\bai\s+training\b/i.test(text) ||
    /\bwant\s+a\s+course\b/i.test(text) ||
    /\bupskill(?:ing)?\b/i.test(text) ||
    (/\badoption\b/i.test(text) && TRAINING_PHRASE.test(text))
  )
}

function mentionedTool(text: string): string | null {
  const match = text.match(/\b(chatgpt|copilot|claude|gemini|n8n|fiken)\b/i)
  return match?.[1] ?? null
}

function inferLevel(text: string): 1 | 2 | 3 | null {
  if (/\b(api|kode|code|database|pgvector|systemet|a system)\b/i.test(text)) return 3
  if (/\b(flyt|flow|workflow|n8n|bygge|build)\b/i.test(text)) return 2
  if (/\b(chatgpt|copilot|claude|gemini|bare bruke|only use|chat)\b/i.test(text)) return 1
  return null
}

function rememberLevel(intake: WorkshopIntake, text: string, at: string): void {
  if (intake.fields.level && !intake.fields.level.gap) return
  const level = inferLevel(text)
  if (level) intake.fields.level = field(String(level), at, false)
}

/**
 * Level 1 uses the tools. «Vant med slike verktøy» is level 1: familiar, not technical.
 * Level 2 builds a workflow. Level 3 implements (developer, code, API) and is the
 * only level that ticks Technical audience.
 */
function inferAudienceLevel(text: string): 1 | 2 | 3 {
  if (/\b(kode|utvikler|developer|ingeniør|engineer|api)\b/i.test(text)) return 3
  if (/\b(flyt|workflow|n8n|bygge|build)\b/i.test(text)) return 2
  return 1
}

function applyAnswer(intake: WorkshopIntake, key: IntakeFieldKey, text: string, at: string): void {
  const kept = keepWorkshopAnswer(text)
  if (kept === 'empty') return
  if (kept === 'gap') {
    intake.fields[key] = field('', at, true)
    if (key === 'stack') intake.fields.models = field('', at, true)
    if (key === 'security') intake.fields.gdpr = field('', at, true)
    if (key === 'audience') intake.fields.level = field('', at, true)
    return
  }
  if (key === 'audience') {
    intake.fields.audience = field(text.trim().slice(0, 160), at, false)
    const audienceLevel = inferAudienceLevel(text)
    const currentLevel = Number(intake.fields.level?.value)
    if (
      !intake.fields.level ||
      intake.fields.level.gap ||
      !Number.isFinite(currentLevel) ||
      audienceLevel > currentLevel
    ) {
      intake.fields.level = field(String(audienceLevel), at, false)
    }
    return
  }
  if (key === 'company') {
    intake.fields.company = field(text.trim().slice(0, 120), at, false)
    return
  }
  if (key === 'stack') {
    intake.fields.stack = field(text.trim().slice(0, 400), at, false)
    intake.fields.models = field(text.trim().slice(0, 400), at, false)
    rememberLevel(intake, text, at)
    return
  }
  if (key === 'security') {
    const noData = isNo(text)
    const value = noData ? NO_PERSONAL_DATA[intake.language] : text.trim().slice(0, 400)
    intake.fields.security = field(value, at, false)
    intake.fields.gdpr = field(value, at, false)
    return
  }
  if (key === 'use') {
    const internal = /\b(internt|intern|selv|own team|internal|inside)\b/i.test(text)
    const external = /\b(utenfor|eksternt|outside|external|kunder|customers)\b/i.test(text)
    const value = internal && !external ? INTERNAL_ONLY[intake.language] : text.trim().slice(0, 400)
    intake.fields.use = field(value, at, false)
    return
  }
  intake.fields[key] = field(text.trim().slice(0, 400), at, false)
  if (key === 'problem' || key === 'goal') rememberLevel(intake, text, at)
}

function topicsToAsk(intake: WorkshopIntake): IntakeFieldKey[] {
  return ASK_ORDER.filter(key => !intake.fields[key])
}

function nextQuestion(intake: WorkshopIntake): string | null {
  if (intake.questionsAsked >= MAX_INTAKE_QUESTIONS) return null
  for (const key of topicsToAsk(intake)) {
    if (intake.fields[key]) continue
    if (intake.questionsAsked >= MAX_INTAKE_QUESTIONS) return null
    intake.pending = key
    intake.questionsAsked += 1
    return intakeQuestion(key, intake.language, intake.askedAs ?? 'workshop')
  }
  return null
}

function finish(intake: WorkshopIntake, reply: string): IntakeAdvanceResult {
  intake.done = true
  intake.pending = null
  return { handled: true, reply, intake, done: true }
}

function seedCompanyName(
  intake: WorkshopIntake,
  companyName: string | null | undefined,
  at: string,
  source: IntakeSource = 'research'
): void {
  const value = companyName?.trim()
  if (!value || value.length < 2 || intake.fields.company) return
  intake.fields.company = field(value.slice(0, 120), at, false, source)
}

const FIXED_CONSULTANT_HOST = 'farzadbayat.com'

function consultantSiteHost(): string {
  try {
    return new URL(CONTACT_CONFIG.WEBSITE_URL).hostname.replace(/^www\./, '').toLowerCase()
  } catch {
    return ''
  }
}

function hostMatches(domain: string, host: string): boolean {
  return domain === host || domain.endsWith(`.${host}`)
}

/**
 * farzad+tag@farzadbayat.com is the test inbox, not the visitor's firm.
 * A *.vercel.app preview keeps that host as the stem even when the site URL
 * is the preview itself.
 * Same function as #324. Grounding imports this one. Do not add another copy.
 */
export function isConsultantSiteEmail(email: string | null | undefined, siteHost?: string): boolean {
  const domain = emailDomain(email)
  if (!domain) return false
  if (hostMatches(domain, FIXED_CONSULTANT_HOST)) return true
  const host = (siteHost ?? consultantSiteHost()).replace(/^www\./, '').toLowerCase()
  if (!host || host === 'vercel.app' || host.endsWith('.vercel.app')) return false
  return hostMatches(domain, host)
}

/** Company stem from a work-email domain. Free and example domains name nothing. */
export function companyNameFromEmail(email: string | null | undefined): string {
  const domain = emailDomain(email)
  if (!domain || isGenericOrFreeDomain(domain)) return ''
  const stem = domain.split('.')[0] ?? ''
  if (stem.length < 2 || !/^[a-z0-9-]+$/i.test(stem)) return ''
  const name = stem.charAt(0).toUpperCase() + stem.slice(1)
  const decision = decideCompanyBinding({
    email: email ?? null,
    candidate: { name, domain, confidence: 1 },
  })
  return decision.status === 'bound' ? decision.companyName : ''
}

/**
 * The plate prefills the company from the email stem. A visitor who types
 * in front of that prefill gets «Demo ASFarzadbayat». Drop the glued stem.
 * A company that is only the stem, or has a space before it, is kept.
 */
export function stripGluedEmailCompany(company: string, email: string | null | undefined): string {
  const value = company.trim()
  const stem = companyNameFromEmail(email)
  if (!stem || value.length <= stem.length || !value.endsWith(stem)) return value
  const head = value.slice(0, value.length - stem.length)
  if (/\s$/.test(head) || head.trim().length < 2) return value
  return head.trim()
}

/**
 * A stored answer wins. The plate name wins next, including on a free inbox.
 * With no email, a hinted name is kept. A free or reserved domain drops every
 * research hint. A name from another entity is dropped for the domain stem.
 */
export function resolveWorkshopCompanyName(input: {
  intake?: WorkshopIntake | null | undefined
  companyName?: string | null | undefined
  plateCompany?: string | null | undefined
  email?: string | null | undefined
  candidateDomain?: string | null | undefined
  confidence?: number | null | undefined
}): string {
  const stored = input.intake?.fields.company
  if (stored && !stored.gap && stored.value.trim().length >= 2) return stored.value.trim()
  const plate = input.plateCompany?.trim() ?? ''
  if (plate.length >= 2) return plate.slice(0, 120)
  const hinted = input.companyName?.trim() ?? ''
  if (!input.email?.includes('@')) return hinted.length >= 2 ? hinted.slice(0, 120) : ''
  if (isGenericOrFreeDomain(emailDomain(input.email))) return ''
  if (hinted.length >= 2) {
    const decision = decideCompanyBinding({
      email: input.email,
      candidate: {
        name: hinted,
        ...(input.candidateDomain ? { domain: input.candidateDomain } : {}),
        confidence: input.confidence ?? 0,
      },
    })
    if (decision.status === 'bound') return decision.companyName
  }
  return companyNameFromEmail(input.email)
}

function knownForTurn(
  known: Partial<Record<IntakeFieldKey, string>> | undefined,
  email: string | null | undefined,
  plateCompany?: string | null
): Partial<Record<IntakeFieldKey, string>> | undefined {
  if (!known?.company) return known
  const plate = plateCompany?.trim() ?? ''
  if (plate.length >= 2 || (email?.includes('@') && isGenericOrFreeDomain(emailDomain(email)))) {
    const rest: Partial<Record<IntakeFieldKey, string>> = { ...known }
    delete rest.company
    return rest
  }
  if (!email?.includes('@')) return known
  const decision = decideCompanyBinding({
    email,
    candidate: { name: known.company, confidence: 0 },
  })
  if (decision.status === 'bound') return known
  const rest: Partial<Record<IntakeFieldKey, string>> = { ...known }
  delete rest.company
  return rest
}

function companySeed(input: {
  companyName?: string | null | undefined
  plateCompany?: string | null | undefined
  email?: string | null | undefined
}): { name: string; source: IntakeSource } | null {
  const plate = input.plateCompany?.trim() ?? ''
  if (plate.length >= 2) return { name: plate.slice(0, 120), source: 'chat' }
  const hinted = input.companyName?.trim() ?? ''
  if (!input.email?.includes('@')) {
    return hinted.length >= 2 ? { name: hinted.slice(0, 120), source: 'research' } : null
  }
  if (isGenericOrFreeDomain(emailDomain(input.email))) return null
  if (hinted.length >= 2) {
    const decision = decideCompanyBinding({
      email: input.email,
      candidate: { name: hinted, confidence: 0 },
    })
    if (decision.status === 'bound') return { name: decision.companyName, source: 'research' }
  }
  const stem = companyNameFromEmail(input.email)
  return stem ? { name: stem, source: 'research' } : null
}

export function advanceWorkshopIntake(input: {
  intake: WorkshopIntake | null
  userText: string
  now: string
  language: IntakeLanguage
  /** Facts a finished research turn already stored. Missing keys are still asked. */
  known?: Partial<Record<IntakeFieldKey, string>>
  /** Discovery brief fields already stated. They replace a research guess and are not re-asked. */
  briefFacts?: Partial<Record<IntakeFieldKey, string>>
  /** Research hint. Kept only when it belongs to the email domain. */
  companyName?: string | null
  /** Company typed on the concierge plate. Wins over research. */
  plateCompany?: string | null
  email?: string | null
}): IntakeAdvanceResult {
  const text = input.userText.trim()
  if (!text) return { handled: false, reply: null, intake: input.intake ?? emptyWorkshopIntake(input.language), done: false }

  const known = knownForTurn(input.known, input.email, input.plateCompany)
  const seeded = companySeed(input)
  let intake = input.intake
  if (!intake || (intake.done && !intake.consent)) {
    if (!isWorkshopAsk(text)) {
      return { handled: false, reply: null, intake: intake ?? emptyWorkshopIntake(input.language), done: false }
    }
    intake = emptyWorkshopIntake(input.language)
    seedResearchFields(intake, known, input.now)
    seedBriefFields(intake, input.briefFacts, input.now)
    seedCompanyName(intake, seeded?.name, input.now, seeded?.source ?? 'research')
    const tool = mentionedTool(text)
    if (tool) {
      intake.fields.stack = field(tool, input.now, false)
      intake.fields.models = field(tool, input.now, false)
      rememberLevel(intake, text, input.now)
    }
    intake.questionsAsked = 1
    intake.askedAs = workshopAskNoun(text)
    if (isCombinedConsultingAndTraining(text)) {
      intake.combinedWithImplementation = true
      intake.pending = 'implementation'
      return { handled: true, reply: implementationAsk(intake.language), intake, done: false }
    }
    intake.pending = 'consent'
    return { handled: true, reply: workshopOpener(intake.language, intake.askedAs), intake, done: false }
  }

  if (intake.done) return { handled: false, reply: null, intake, done: true }

  intake.language = input.language

  if (wantsStop(text)) {
    intake.stopped = true
    return finish(intake, STOPPED[intake.language])
  }

  if (intake.pending === 'implementation') {
    const kept = keepWorkshopAnswer(text)
    if (kept === 'keep') {
      intake.fields.problem = field(text.trim().slice(0, 400), input.now, false)
    }
    intake.pending = 'consent'
    return {
      handled: true,
      reply: workshopOpener(intake.language, intake.askedAs ?? 'kurs'),
      intake,
      done: false,
    }
  }

  if (intake.pending === 'consent') {
    if (isNo(text) && !isYes(text)) {
      intake.consent = false
      intake.stopped = true
      intake.fields = {}
      return finish(intake, DECLINED[intake.language])
    }
    if (!isYes(text) && !isUnknown(text)) {
      // Not a yes or no. Do not repeat the opener. Let the agent answer this turn.
      // Consent stays pending, so a later «ja» still starts the questions.
      return { handled: false, reply: null, intake, done: false }
    }
    intake.consent = true
    seedCompanyName(intake, seeded?.name, input.now, seeded?.source ?? 'research')
    if (!isYes(text) || text.split(/\s+/).length > 2) applyLoose(intake, text, input.now)
    const question = nextQuestion(intake)
    if (!question) return finish(intake, workshopCloseReply(intake.language))
    return { handled: true, reply: question, intake, done: false }
  }

  // A side question («Haha, kan Gemini lage kaffe også?») is not an answer.
  // Keep the slot open and let the agent reply to it.
  if (intake.pending && isSideQuestion(text)) {
    return { handled: false, reply: null, intake, done: false }
  }

  if (intake.pending) {
    applyAnswer(intake, intake.pending, text, input.now)
  }
  seedCompanyName(intake, seeded?.name, input.now, seeded?.source ?? 'research')
  const question = nextQuestion(intake)
  if (!question) return finish(intake, workshopCloseReply(intake.language))
  return { handled: true, reply: question, intake, done: false }
}

const SIDE_QUESTION_START =
  /^(?:(?:haha|hehe|hah|lol|hihi)\b[\s,!.]*)?(?:kan|hva|hvordan|hvorfor|hvem|er|blir|vil|gjør|can|what|how|why|who|is|does|will|do)\b/i

function isSideQuestion(text: string): boolean {
  const value = text.trim()
  return value.endsWith('?') && SIDE_QUESTION_START.test(value)
}

function applyLoose(intake: WorkshopIntake, text: string, at: string): void {
  const tool = mentionedTool(text)
  if (tool && !intake.fields.stack) {
    intake.fields.stack = field(tool, at, false)
    intake.fields.models = field(tool, at, false)
  }
  rememberLevel(intake, text, at)
}

function fieldMissing(intake: WorkshopIntake, key: IntakeFieldKey): boolean {
  const row = intake.fields[key]
  return !row || row.gap || !row.value.trim()
}

export function reviewGapKeys(intake: WorkshopIntake | null): IntakeFieldKey[] {
  const keys: IntakeFieldKey[] = [
    'company',
    'stack',
    'models',
    'security',
    'gdpr',
    'use',
    'goal',
    'level',
    'problem',
    'audience',
  ]
  if (!intake) return keys
  // A draft from the session has no consent, but its filled fields are still filled.
  return keys.filter(key => fieldMissing(intake, key))
}

export function reviewGapLines(
  intake: WorkshopIntake | null,
  language: IntakeLanguage,
  resolved?: { companyName?: string | null | undefined }
): string[] {
  const labels: Record<IntakeFieldKey, Record<IntakeLanguage, string>> = {
    company: { nb: 'Bedrift mangler.', nn: 'Bedrift manglar.', en: 'Company is missing.' },
    stack: { nb: 'Stakk mangler.', nn: 'Stakk manglar.', en: 'Stack is missing.' },
    models: { nb: 'Modeller mangler.', nn: 'Modellar manglar.', en: 'Models are missing.' },
    security: {
      nb: 'Personopplysninger er ukjent.',
      nn: 'Personopplysningar er ukjende.',
      en: 'Personal data is unknown.',
    },
    gdpr: {
      nb: 'Personopplysninger er ukjent.',
      nn: 'Personopplysningar er ukjende.',
      en: 'Personal data is unknown.',
    },
    use: { nb: 'Bruk mangler.', nn: 'Bruk manglar.', en: 'Use is missing.' },
    goal: { nb: 'Mål mangler.', nn: 'Mål manglar.', en: 'Goal is missing.' },
    level: { nb: 'Nivå er ikke kjent.', nn: 'Nivå er ikkje kjent.', en: 'Level is not known.' },
    problem: { nb: 'Oppgave mangler.', nn: 'Oppgåve manglar.', en: 'The task is missing.' },
    audience: { nb: 'Deltakere mangler.', nn: 'Deltakarar manglar.', en: 'Audience is missing.' },
  }
  const companyKnown = (resolved?.companyName?.trim().length ?? 0) >= 2
  const keys = reviewGapKeys(intake).filter(key => {
    if (key === 'company' && companyKnown) return false
    if (key === 'models') return false
    if (key === 'gdpr') return false
    if (key === 'level' && intake && fieldMissing(intake, 'audience')) return false
    return true
  })
  const lines = keys.map(key => labels[key][language])
  return [...new Set(lines)]
}

export const GENERIC_PROBLEM_TODAY: Record<IntakeLanguage, string> = {
  nb: 'Dere gjør den samme oppgaven på nytt hver gang.',
  nn: 'De gjer den same oppgåva på nytt kvar gong.',
  en: 'You do the same task from scratch every time.',
}

/** The opener is the first turn. Persist it then, not after the 15-minute outbox drain. */
export function shouldPersistWorkshopIntake(intake: WorkshopIntake | null | undefined): boolean {
  if (!intake) return false
  if (intake.consent || intake.stopped) return true
  return intake.pending === 'consent' || intake.questionsAsked > 0
}

export type IntakeTurnChannel = 'text' | 'voice'
export type IntakeTurnPath = 'local' | 'live'

/**
 * Typed turns stay in the browser. Voice stays on Live until Farzad deploys it.
 * The browser script does not call Fly.
 */
export function intakePathForTurn(channel: IntakeTurnChannel): IntakeTurnPath {
  if (channel === 'voice') return 'live'
  return workshopLiveToolEnabled() ? 'live' : 'local'
}

export const WORKSHOP_CUSTOMER_ERROR: Record<IntakeLanguage, string> = {
  nb: 'Jeg fikk ikke lagret svaret. Prøv én gang til.',
  nn: 'Eg fekk ikkje lagra svaret. Prøv ein gong til.',
  en: 'I could not save that answer. Please try once more.',
}

/** Live tool stays off unless Farzad sets this. The browser script does not call Fly. */
export function workshopLiveToolEnabled(): boolean {
  const vite = (import.meta as { env?: Record<string, string | undefined> }).env?.VITE_WORKSHOP_INTAKE_LIVE_TOOL
  return vite === 'true'
}
